import { useMemo, useState, type ChangeEvent } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, MenuItem, Pagination, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { Karta } from '@/components/Karta';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import { formatGrosze } from '@/lib/format';
import { useTransakcjeParytet, useTransakcjeParytetSlowniki } from '@/lib/useAnalytics';
import type { ParytetWiersz, ParytetZapytanie } from '@/lib/api/analytics';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * TRANSAKCJE — PARYTET ZE STARYM PANELEM. ZIARNO: POZYCJA ZAMÓWIENIA.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * JEDEN WIERSZ = JEDNA POZYCJA ZAMÓWIENIA, nie jedna płatność. Dowód ze starego panelu
 * (`transactions.viewmodel.ts`): kluczem wiersza jest `orderPositionId`, a `addOrUpdateTransaction`
 * wyszukuje po nim istniejący rekord. Gdyby wiersz był transakcją, ten kod nie miałby czego szukać.
 *
 * KOLUMNY SĄ ZMIERZONE, NIE ZAPROJEKTOWANE. Jest ich jedenaście i dokładnie te:
 *   ☐ · Data · Data dostawy · Klient · Szkoła · Szczegóły · Dostawca · Kwota · Status · Typ · Akcje
 *
 * IDENTYFIKATOR TECHNICZNY NIE JEST KOLUMNĄ. Stary panel trzyma `orderPositionId` w nagłówku typu
 * `checkAll` — to zaznaczanie, nie dana do czytania. Identyfikatory jeżdżą w danych wiersza
 * i służą akcjom; operator ich nie ogląda.
 *
 * ┌─ DLACZEGO KWOTA BYWA PUSTA I DLACZEGO TO DOBRZE ────────────────────────────────────────────┐
 * │ „Kwota" to `OrderPosition.PayedAmount` — ile REALNIE pobrano. Replikacja tego pola należy   │
 * │ do innego strumienia (R5-04) i jeszcze trwa. Zamiast czekać, ekran działa i JAWNIE mówi,    │
 * │ czego nie wie.                                                                              │
 * │                                                                                             │
 * │ Podstawienie ceny katalogowej (`price_grosze`) dałoby tabelę, która wygląda na kompletną    │
 * │ i kłamie o pieniądzach — obie liczby rozjeżdżają się przy KAŻDEJ zniżce szkolnej, czyli     │
 * │ dokładnie tam, gdzie operator patrzy przy reklamacji. Pusta komórka z powodem jest uczciwa; │
 * │ wypełniona złą liczbą nie jest.                                                             │
 * └─────────────────────────────────────────────────────────────────────────────────────────────┘
 */


function dzisiaj(): string {
  return new Date().toISOString().slice(0, 10);
}

function formatData(v: string | null, zGodzina: boolean): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return zGodzina ? d.toLocaleString('pl-PL') : d.toLocaleDateString('pl-PL');
}

/**
 * Komórka „Kwota".
 *
 * Trzy stany, trzy różne komunikaty. Zlanie ich w jeden („brak danych") schowałoby różnicę
 * między „ta pozycja jeszcze nie dojechała" a „cała funkcja nie jest jeszcze podłączona" —
 * a to pierwsze mija samo, drugie wymaga cudzej pracy.
 */
function Kwota({ w }: { w: ParytetWiersz }) {
  if (w.kwota_stan === 'ZNANA' && w.kwota_grosze !== null) {
    return <>{formatGrosze(w.kwota_grosze)}</>;
  }
  const powod = w.kwota_stan === 'KOLUMNA_NIE_ISTNIEJE'
    ? 'Kwota pobrana nie jest jeszcze replikowana do CRM (zależność: strumień R5-04). '
      + 'Cena katalogowa NIE jest tu podstawiana — byłaby inną liczbą niż ta, którą pobrano.'
    : 'Ta pozycja nie ma jeszcze zreplikowanej kwoty pobranej. Backfill historyczny w toku.';
  return (
    <Tooltip title={powod}>
      <Typography component="span" variant="body2" sx={{ color: 'text.disabled', cursor: 'help' }}>
        niedostępne
      </Typography>
    </Tooltip>
  );
}

export function TransakcjeParytetPage() {
  const slowniki = useTransakcjeParytetSlowniki();

  const [od, setOd] = useState(dzisiaj());
  const [doo, setDoo] = useState(dzisiaj());
  const [klient, setKlient] = useState('');
  const [szkola, setSzkola] = useState('');
  const [dostawca, setDostawca] = useState('');
  const [kwota, setKwota] = useState('');
  const [status, setStatus] = useState('');
  // Stary panel USTAWIA ten filtr domyślnie (`headers[9].search = 'Zamówienie / Płatność portfelem'`).
  const [typ, setTyp] = useState('Zamówienie / Płatność portfelem');

  const [sort, setSort] = useState('data');
  const [kierunek, setKierunek] = useState<'ASC' | 'DESC'>('DESC');
  const [strona, setStrona] = useState(1);
  const [zaznaczone, setZaznaczone] = useState<Set<number>>(new Set());

  const zapytanie: ParytetZapytanie = useMemo(() => ({
    date_from: od, date_to: doo,
    klient: klient || undefined,
    szkola: szkola || undefined,
    dostawca: dostawca || undefined,
    kwota: kwota || undefined,
    status: status || undefined,
    typ: typ || undefined,
    sort, kierunek, strona,
  }), [od, doo, klient, szkola, dostawca, kwota, status, typ, sort, kierunek, strona]);

  const { data, isLoading, error } = useTransakcjeParytet(zapytanie);

  /**
   * Kliknięcie w nagłówek.
   *
   * PIERWSZE kliknięcie w kolumnę daje MALEJĄCO — tak jak stary panel, którego domyślnym
   * stanem jest `headers[1].isSortedDescending = true`. Rosnąco pojawia się dopiero przy
   * drugim kliknięciu w tę samą kolumnę. Jedno sortowanie naraz.
   */
  function sortuj(kolumna: string) {
    if (sort === kolumna) setKierunek((k) => (k === 'DESC' ? 'ASC' : 'DESC'));
    else { setSort(kolumna); setKierunek('DESC'); }
    setStrona(1);
  }

  function zmienFiltr(ustaw: (v: string) => void) {
    return (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => { ustaw(e.target.value); setStrona(1); };
  }

  const wiersze = data?.wiersze ?? [];
  const stron = Math.max(1, Math.ceil((data?.wierszy_ogolem ?? 0) / (data?.rozmiar_strony ?? 10)));

  function przelacz(id: number) {
    setZaznaczone((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }

  return (
    <Stack spacing={2}>
      <NaglowekEkranu
        tytul="Transakcje"
        opis="Rejestr pozycji zamówień — jeden wiersz to jedna pozycja, tak jak w starym panelu."
      />

      {data?.zaleznosc && (
        <Alert severity="info">
          <strong>Kolumna „Kwota" czeka na dane.</strong> {data.zaleznosc.skutek}{' '}
          Brakuje: <code>{data.zaleznosc.czego_brak}</code> (strumień {data.zaleznosc.strumien}).
          Cena katalogowa nie jest podstawiana — byłaby inną liczbą niż ta, którą pobrano.
        </Alert>
      )}

      <Karta>
        <Stack direction="row" spacing={1} useFlexGap sx={{ p: 2, flexWrap: 'wrap' }}>
          <TextField label="Data od" type="date" size="small" value={od}
            onChange={zmienFiltr(setOd)} slotProps={{ inputLabel: { shrink: true } }} />
          <TextField label="Data do" type="date" size="small" value={doo}
            onChange={zmienFiltr(setDoo)} slotProps={{ inputLabel: { shrink: true } }} />
          <TextField label="Klient" size="small" value={klient} onChange={zmienFiltr(setKlient)} />
          <TextField label="Szkoła" size="small" value={szkola} onChange={zmienFiltr(setSzkola)} />
          <TextField label="Dostawca" size="small" value={dostawca} onChange={zmienFiltr(setDostawca)} />
          <TextField label="Kwota" size="small" value={kwota} onChange={zmienFiltr(setKwota)}
            disabled={!data?.zrodlo_kwoty}
            helperText={data?.zrodlo_kwoty ? undefined : 'brak źródła kwoty'} />
          <TextField label="Status" size="small" select sx={{ minWidth: 160 }} value={status}
            onChange={zmienFiltr(setStatus)}>
            <MenuItem value="">wszystkie</MenuItem>
            {(slowniki.data?.statusy ?? []).map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          <TextField label="Typ" size="small" select sx={{ minWidth: 240 }} value={typ}
            onChange={zmienFiltr(setTyp)}>
            <MenuItem value="">wszystkie</MenuItem>
            {(slowniki.data?.typy ?? []).map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
        </Stack>
      </Karta>

      {isLoading && <LoadingState />}
      {error ? <ErrorState error={error} context="Ekran Transakcji — parytet ze starym panelem." /> : null}

      {!isLoading && !error && wiersze.length === 0 && (
        <EmptyState
          title="Brak pozycji w tym zakresie"
          description="Domyślnie pokazywany jest dzisiejszy dzień — tak jak w starym panelu. Zmień zakres dat, żeby zobaczyć wcześniejsze."
        />
      )}

      {!isLoading && !error && wiersze.length > 0 && (
        <Karta>
          <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="Transakcje — pozycje zamówień">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  {[
                    ['data', 'Data'], [null, 'Data dostawy'], ['klient', 'Klient'],
                    ['szkola', 'Szkoła'], ['szczegoly', 'Szczegóły'], ['dostawca', 'Dostawca'],
                    ['kwota', 'Kwota'], ['status', 'Status'], ['typ', 'Typ'], [null, 'Akcje'],
                  ].map(([klucz, etykieta]) => (
                    <TableCell
                      key={etykieta as string}
                      align={klucz === 'kwota' ? 'right' : 'left'}
                      onClick={klucz ? () => sortuj(klucz as string) : undefined}
                      sx={{ whiteSpace: 'nowrap', cursor: klucz ? 'pointer' : 'default',
                            userSelect: 'none', fontWeight: 600 }}
                    >
                      {etykieta}
                      {klucz && sort === klucz && (kierunek === 'DESC'
                        ? <ArrowDownwardIcon fontSize="inherit" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                        : <ArrowUpwardIcon fontSize="inherit" sx={{ ml: 0.5, verticalAlign: 'middle' }} />)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {wiersze.map((w) => (
                  <TableRow key={w.pozycja_id} hover
                    sx={w.pozycja_skasowana ? { opacity: 0.6 } : undefined}>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={zaznaczone.has(w.pozycja_id)}
                        onChange={() => przelacz(w.pozycja_id)} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatData(w.data, true)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatData(w.data_dostawy, false)}</TableCell>
                    <TableCell>{w.klient ?? '—'}</TableCell>
                    <TableCell>{w.szkola ?? '—'}</TableCell>
                    <TableCell>
                      {w.szczegoly ?? '—'}
                      {w.ilosc !== null && w.ilosc > 1 && (
                        <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'text.secondary' }}>
                          ×{w.ilosc}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{w.dostawca ?? '—'}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}><Kwota w={w} /></TableCell>
                    <TableCell>
                      <Chip size="small" label={w.status ?? '—'}
                        color={w.pozycja_skasowana ? 'default' : undefined} />
                    </TableCell>
                    <TableCell>{w.typ ?? '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {/*
                        Zwrot i Usuń to operacje ZAPISU. Control Center pracuje w trybie
                        PROD_READONLY, w którym zapis jest wyłączony technicznie — przycisk,
                        który wygląda na czynny i kończy się błędem, byłby gorszy niż przycisk
                        wyłączony z podaniem powodu.
                      */}
                      <Tooltip title="Tryb tylko do odczytu — operacje zapisu są wyłączone.">
                        <span>
                          <Button size="small" disabled>Zwrot</Button>
                          <Button size="small" color="error" disabled>Usuń</Button>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction="row" spacing={2} useFlexGap
            sx={{ p: 2, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="subtitle2">SUMA TRANSAKCJI</Typography>
              <Typography variant="h6">
                {data?.suma.grosze === null ? 'niedostępna' : formatGrosze(data!.suma.grosze!)}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {/*
                  Suma liczy się po WSZYSTKICH przefiltrowanych wierszach, nie po bieżącej
                  stronie, i pomija pozycje skasowane. Gdy część wierszy nie ma jeszcze kwoty,
                  suma jest NIEPEŁNA i musi to o sobie powiedzieć — liczba bez tego zastrzeżenia
                  byłaby twierdzeniem o pieniądzach, którego nie ma czym poprzeć.
                */}
                {data?.suma.kompletna
                  ? `${data.suma.wierszy_liczonych} poz. (bez skasowanych), wszystkie filtry uwzględnione`
                  : `NIEPEŁNA — ${data?.suma.wierszy_bez_kwoty ?? 0} z ${data?.suma.wierszy_liczonych ?? 0} poz. bez zreplikowanej kwoty`}
              </Typography>
            </Box>

            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
              {zaznaczone.size > 0 && (
                <Chip size="small" label={`zaznaczono: ${zaznaczone.size}`}
                  onDelete={() => setZaznaczone(new Set())} />
              )}
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {data?.wierszy_ogolem ?? 0} poz.
              </Typography>
              <Pagination size="small" count={stron} page={strona}
                onChange={(_, s) => setStrona(s)} />
            </Stack>
          </Stack>
        </Karta>
      )}
    </Stack>
  );
}

export default TransakcjeParytetPage;
